import { gql } from "@apollo/client";
import { sharedFeeTransactionFields, workflowProductTotals } from "../fragments/feeTransaction";

const FEE_TRANSACTIONS = gql`
  query FeeTransactions($productId: ID, $assetId: ID, $transactionId: ID, $includeDeleted: Boolean) {
    feeTransactions(productId: $productId, assetId: $assetId, transactionId: $transactionId, includeDeleted: $includeDeleted) {
      ... on AltInvestmentFeeTransaction {
        ${sharedFeeTransactionFields}
      }
      ... on BrandingFeeTransaction {
        _id
        createdAt
        dateReceived
        dateRequested
        lastInvoicedAt
        feeType
        receivedAmount
        requestedAmount
        status
        queue
        holdNote
        quickbooksInvoiceNumber
        waivedAmount
        meta {
          notes
          isDeleted
        }
        denormalized {
          payerName
          productName
          assetName
        }
        product {
          ... on BrandingProduct {
            _id
            name
          }
        }
        entities {
          _id
          entity {
            __typename
            ... on FirmEntity {
              _id
              entityName
            }
            ... on PartnerEntity {
              _id
              entityName
            }
            ... on BranchEntity {
              _id
              entityName
            }
          }
        }
      }
      ... on AdminFeeTransaction {
        ${sharedFeeTransactionFields}
      }
      ... on ArrearFeeTransaction {
        ${sharedFeeTransactionFields}
      }
      ... on EntityDueDiligenceFeeTransaction {
        ${sharedFeeTransactionFields}
        entity {
          __typename
          ... on OrganizationEntity {
          _id
          entityName
          }
        }
      }
      ... on ExpediteFeeTransaction {
        ${sharedFeeTransactionFields}
        transaction {
          ... on GrantTransaction {
            _id
            transactionAmount
          }
        }
      }
      ... on GiftAgreementFeeTransaction {
        ${sharedFeeTransactionFields}
      }
      ... on ImpactInvestmentFeeTransaction {
        ${sharedFeeTransactionFields}
      }
      ... on ReimbursementFeeTransaction {
        ${sharedFeeTransactionFields}
        description
      }
      ... on MiscFeeTransaction {
        ${sharedFeeTransactionFields}
        description
      }
      ... on ExpenditureResponsibilityFeeTransaction {
        ${sharedFeeTransactionFields}
      }
    }
  }
`;

const FEE_TRANSACTIONS_BY_FIRM = gql`
  query FeeTransactionsByFirm($entityId: ID!, $quarter: Int!, $year: Int!) {
    feeTransactionsByFirm(entityId: $entityId, quarter: $quarter, year: $year) {
      ... on AltInvestmentFeeTransaction {
        _id
        year
        quarter
        feeType
        status
      }
      ... on AdminFeeTransaction {
        _id
        year
        quarter
        feeType
        status
      }
      ... on ArrearFeeTransaction {
        _id
        year
        quarter
        feeType
        status
      }
      ... on EntityDueDiligenceFeeTransaction {
        _id
        year
        quarter
        feeType
        status
      }
      ... on ExpediteFeeTransaction {
        _id
        year
        quarter
        feeType
        status
      }
      ... on GiftAgreementFeeTransaction {
        _id
        year
        quarter
        feeType
        status
      }
      ... on ImpactInvestmentFeeTransaction {
        _id
        year
        quarter
        feeType
        status
      }
      ... on ReimbursementFeeTransaction {
        _id
        year
        quarter
        feeType
        status
      }
      ... on MiscFeeTransaction {
        _id
        year
        quarter
        feeType
        status
      }
      ... on ExpenditureResponsibilityFeeTransaction {
        _id
        year
        quarter
        feeType
        status
      }
    }
  }
`;

const FEE_TRANSACTIONS_WITHOUT_ADVISOR = gql`
  query FeeTransactionsByDonorWithoutAdvisor($quarter: Int!, $year: Int!) {
    feeTransactionsByDonorWithoutAdvisor(quarter: $quarter, year: $year) {
      ... on AltInvestmentFeeTransaction {
        _id
      }
      ... on AdminFeeTransaction {
        _id
      }
      ... on ArrearFeeTransaction {
        _id
      }
      ... on EntityDueDiligenceFeeTransaction {
        _id
      }
      ... on ExpediteFeeTransaction {
        _id
      }
      ... on GiftAgreementFeeTransaction {
        _id
      }
      ... on ImpactInvestmentFeeTransaction {
        _id
      }
      ... on ReimbursementFeeTransaction {
        _id
      }
      ... on MiscFeeTransaction {
        _id
      }
      ... on ExpenditureResponsibilityFeeTransaction {
        _id
      }
    }
  }
`;

const FIND_FEE_TRANSACTIONS_SERVER_SIDE = gql`
  query FindFeeTransactionsServerSide($input: ServerQueryInput, $productId: ID) {
    findFeeTransactionsServerSide(input: $input, productId: $productId) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      feeTransactions {
        ... on AltInvestmentFeeTransaction {
          ${sharedFeeTransactionFields}
        }
        ... on BrandingFeeTransaction {
          _id
          createdAt
          dateReceived
          dateRequested
          lastInvoicedAt
          feeType
          receivedAmount
          requestedAmount
          status
          queue
          holdNote
          quickbooksInvoiceNumber
          waivedAmount
          meta {
            notes
          }
          denormalized {
            payerName
            productName
            assetName
          }
          product {
            ... on BrandingProduct {
              _id
              name
            }
          }
          entities {
            _id
            entity {
              __typename
              ... on FirmEntity {
                _id
                entityName
              }
              ... on PartnerEntity {
                _id
                entityName
              }
              ... on BranchEntity {
                _id
                entityName
              }
            }
          }
        }
        ... on AdminFeeTransaction {
          ${sharedFeeTransactionFields}
        }
        ... on ArrearFeeTransaction {
          ${sharedFeeTransactionFields}
        }
        ... on EntityDueDiligenceFeeTransaction {
          ${sharedFeeTransactionFields}
          entity {
            __typename
            ... on OrganizationEntity {
              _id
              entityName
            }
          }
        }
        ... on ExpediteFeeTransaction {
          ${sharedFeeTransactionFields}
          transaction {
            ... on GrantTransaction {
              _id
              transactionAmount
            }
          }
        }
        ... on GiftAgreementFeeTransaction {
          ${sharedFeeTransactionFields}
        }
        ... on ImpactInvestmentFeeTransaction {
          ${sharedFeeTransactionFields}
        }
        ... on ReimbursementFeeTransaction {
          ${sharedFeeTransactionFields}
          description
        }
        ... on MiscFeeTransaction {
          ${sharedFeeTransactionFields}
          description
        }
        ... on ExpenditureResponsibilityFeeTransaction {
          ${sharedFeeTransactionFields}
        }
      }
    }
  }
`;

const FIND_FEE_WORKFLOW_FEES = gql`
  query FindFeeWorkflowFees($bucket: FeeWorkflowBucket!, $input: ServerQueryInput, $readyOnly: Boolean) {
    findFeeWorkflowFees(bucket: $bucket, input: $input, readyOnly: $readyOnly) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      feeTransactions {
        ... on AltInvestmentFeeTransaction {
          ${sharedFeeTransactionFields}
          ${workflowProductTotals}
        }
        ... on BrandingFeeTransaction {
          ${sharedFeeTransactionFields}
          product {
            ... on BrandingProduct {
              _id
              name
            }
          }
          entities {
            _id
            entity {
              __typename
              ... on FirmEntity {
                _id
                entityName
              }
              ... on PartnerEntity {
                _id
                entityName
              }
              ... on BranchEntity {
                _id
                entityName
              }
            }
          }
        }
        ... on AdminFeeTransaction {
          ${sharedFeeTransactionFields}
          ${workflowProductTotals}
        }
        ... on ArrearFeeTransaction {
          ${sharedFeeTransactionFields}
          ${workflowProductTotals}
        }
        ... on EntityDueDiligenceFeeTransaction {
          ${sharedFeeTransactionFields}
          ${workflowProductTotals}
          entity {
            __typename
            ... on OrganizationEntity {
              _id
              entityName
            }
          }
        }
        ... on ExpediteFeeTransaction {
          ${sharedFeeTransactionFields}
          ${workflowProductTotals}
        }
        ... on GiftAgreementFeeTransaction {
          ${sharedFeeTransactionFields}
          ${workflowProductTotals}
        }
        ... on ImpactInvestmentFeeTransaction {
          ${sharedFeeTransactionFields}
          ${workflowProductTotals}
        }
        ... on ReimbursementFeeTransaction {
          ${sharedFeeTransactionFields}
          ${workflowProductTotals}
          description
        }
        ... on MiscFeeTransaction {
          ${sharedFeeTransactionFields}
          ${workflowProductTotals}
          description
        }
        ... on ExpenditureResponsibilityFeeTransaction {
          ${sharedFeeTransactionFields}
          ${workflowProductTotals}
        }
      }
    }
  }
`;

const FEE_WORKFLOW_COUNTS = gql`
  query FeeWorkflowCounts {
    feeWorkflowCounts {
      due
      inTransit
      onHold
      pastDue
      dueAmount
      inTransitAmount
      onHoldAmount
      pastDueAmount
    }
  }
`;

const FEE_WORKFLOW_METRICS = gql`
  query FeeWorkflowMetrics($year: Int!) {
    feeWorkflowMetrics(year: $year) {
      year
      invoicedYtd
      openAr {
        due
        inTransit
        onHold
        pastDue
      }
      rows {
        feeType
        accountCount
        q1
        q2
        q3
        q4
        ytd
      }
    }
  }
`;

export {
  FEE_TRANSACTIONS,
  FEE_TRANSACTIONS_BY_FIRM,
  FEE_TRANSACTIONS_WITHOUT_ADVISOR,
  FIND_FEE_TRANSACTIONS_SERVER_SIDE,
  FIND_FEE_WORKFLOW_FEES,
  FEE_WORKFLOW_COUNTS,
  FEE_WORKFLOW_METRICS,
};
